/**
 * ¿Esta marca tiene portal del consumidor?
 *
 * Es la consulta que decide si la sección **existe**. ADR-011: el portal es
 * configurable por tenant, viene apagado por defecto y está **prohibido en
 * tenants farmacéuticos** — guardar qué medicamentos escaneó una persona
 * identificada es un dato de salud, categoría especial bajo GDPR, LGPD y la
 * Ley 6534/2020.
 *
 * En una marca farmacéutica la respuesta trae `portal_enabled: false` con
 * `reason: "industry_restricted"`, y el resto de los endpoints del portal
 * devuelven 404 — no 403: para esa marca el portal no está apagado, no
 * existe. La navegación hace lo mismo: `AdminNav` no dibuja la solapa, en vez
 * de dibujarla deshabilitada. Una solapa gris invita a preguntar cómo se
 * habilita; en farma la respuesta no es "se habilita".
 *
 * `GET /consumer/portal/config` es público y recibe el slug por parámetro:
 * lo llama el navegador de alguien que no tiene sesión. Acá el slug sale del
 * claim `tenant` de la sesión del dashboard, que es la marca que se está
 * mirando.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ApiError } from '@/api/errors';
import { consumerPortalKeys } from '@/api/keys';
import { useAuth } from '@/auth/useAuth';
import type { PortalConfig } from './portalApi';
import { portalRequest } from './portalApi';

export function usePortalConfig(): UseQueryResult<PortalConfig, ApiError> {
  const { claims } = useAuth();
  const tenant = claims?.tenant ?? '';

  return useQuery<PortalConfig, ApiError>({
    queryKey: consumerPortalKeys.config(tenant),
    queryFn: ({ signal }) =>
      portalRequest<PortalConfig>('/consumer/portal/config', { query: { tenant }, signal }),
    enabled: tenant.length > 0,
    // La industria de una marca no cambia mientras alguien mira el dashboard.
    staleTime: 10 * 60_000,
  });
}

/**
 * `true` sólo si el servidor dijo que sí. Mientras se resuelve —o si la
 * consulta falla— devuelve `false`: ante la duda, la sección no se muestra.
 * El error opuesto es ofrecer un portal en una marca donde es ilegal.
 */
export function isPortalEnabled(query: UseQueryResult<PortalConfig, ApiError>): boolean {
  return query.data?.portal_enabled === true;
}
